import { FONT_MONO } from '../../config/fonts';
import React, { useMemo } from "react";
import { Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { Typography } from "@mui/material";
import { format } from "date-fns";

import { getCountryInfo, toDMS, } from "../../utils/mapUtils";
import { ShowNumberModes, SpeedUnits } from "../../config/types";

function getColorByOutpostNum(outpostNum) {
  // Map outpostNum to a hue (0-360)
  const hue = (parseInt(outpostNum, 10) * 47) % 360;
  return `hsl(${hue}, 100%, 50%)`;
}

function formatHHmm(date) {
  if (!date) return "";

  const d = new Date(date);

  return `${String(d.getHours()).padStart(2,"0")}${String(d.getMinutes()).padStart(2,"0")}`;
}

function createLabelHTML(target, mode, opacity)
{
  if (mode === ShowNumberModes.NONE) return '';
  
  const hhmm = formatHHmm(target.modifiedAt);
  const outpostColor = getColorByOutpostNum(target.outpostNums[0]);

  let displayNumber = '';
  if (mode === ShowNumberModes.SYNTH) {
    displayNumber = ` ${target.number} `;
  } else if (mode === ShowNumberModes.TRACK) {
    if (target.outpostNums.length > 1) {
      displayNumber = `${target.number}(${target.trackId})`;
    } else {
      displayNumber = `${target.number}(${target.relatedTargets[0]?.TGT_NUM})`;
    }
  }

  if (target.outpostNums.length > 1) {
    return `
      <span style="
        margin-left: 1px;
        font-size: 10px;
        color: yellow;
        text-shadow: 1px 1px 2px black;
        display: flex;
        flex-direction: column;
        align-items: center;
        line-height: 1;
        white-space: nowrap;
        font-family: ${FONT_MONO};
        filter: opacity(${opacity});
      ">
        <span style="border-bottom: 1px solid yellow; padding: 0 2px;">${displayNumber}</span>
        <span>${hhmm}</span>          
      </span>
    `;
  } else {
    return `
      <div style="filter: opacity(${opacity});">
        <span style="
          margin-left: 1px;
          font-size: 10px;
          color: ${outpostColor};;
          text-shadow: 1px 1px 2px black;
          display: flex;
          flex-direction: column;
          align-items: center;
          line-height: 1;
          white-space: nowrap;
          font-family: ${FONT_MONO};
        ">
          <span style="border-bottom: 1px solid ${outpostColor}; padding: 0 2px;">${displayNumber}</span>
          <span>${hhmm}</span>          
        </span>
      </div>
      ${mode !== ShowNumberModes.SYNTH ? `
      <div style="filter: opacity(${opacity});">
        <span style="
          margin-left: 1px;
          font-size: 10px;
          color: ${outpostColor};;
          text-shadow: 1px 1px 2px black;
          display: flex;
          flex-direction: column;
          align-items: center;
          line-height: 1;
          white-space: nowrap;
          font-family: ${FONT_MONO};
        ">
          <span>${target.relatedTargets[0]?.outpostNum}</span>
        </span>
      </div>
      ` : ''}
    `;
  }
}

const FlightMarkerItem = ({
    target,
    position,
    size,
    opacity,
    colorFilter,
    showFlag,
    showNumberMode,
    speedUnit,
    selectedTargetId,
    setSelectedTargetId
})=>{
  const address =
    target.address === 2124415080 ||
    target.address >= 0xFFFFFF
    ?
    null
    :
    target.address;

  const countryInfo = getCountryInfo(address);

  const hasCountry = countryInfo && countryInfo.iso && countryInfo.iso !== "xx";

  const addressHex =
    address !== null
      ? `0x${Number(address)
          .toString(16)
          .toUpperCase()
          .padStart(6, "0")}`
      : "";

  const content =
    target.content != null && Number(target.content) < 0xFFFF
      ? Number(target.content)
      : null;

  const contentOct =
    content !== null
      ? content.toString(8).padStart(4, "0")
      : "";

  const icon = useMemo(()=>{
    return L.divIcon({
      className:"rotated-plane-icon",
      html:`
      <div style="
        position:relative;
        display:flex;
        align-items:center;
      ">
        ${
          target.set_off===0x55
          ?
          `
          <div
            class="important-circle-blink"
            style="
            position:absolute;
            left:50%;
            top:50%;
            width:${size}px;
            height:${size}px;
            transform:translate(-50%,-50%);
            border:2px solid red;
            border-radius:50%;
            z-index:1;
          ">
          </div>
          `
          :
          ""
        }
        <img
          src="/assets/plane.svg"
          style="
          width:${size}px;
          height:${size}px;
          transform:rotate(${target.direction}deg);
          opacity:${opacity};
          filter:
          drop-shadow(5px 2px 3px rgba(0,0,0,.8))
          brightness(0)
          saturate(100%)
          ${colorFilter};
          "
        />

        ${
          showFlag && hasCountry
          ?
          `
          <span
            class="fi fi-${countryInfo.iso}"
            style="
            position:absolute;
            left:-${size*0.6}px;
            top:50%;
            transform:
            translateY(-50%)
            scale(${size/35});
            opacity:${opacity};
          ">
          </span>
          `
          :
          ""
        }
        ${createLabelHTML(
          target,
          showNumberMode,
          opacity
        )}
      </div>
      `,
      iconSize: [size, size],
      iconAnchor:[size/2, size/2]
    });
  },[
    target,
    size,
    opacity,
    colorFilter,
    showFlag,
    showNumberMode,
    hasCountry
  ]);

  return (
    <Marker
      position={position}
      icon={icon}
      eventHandlers={{
        mouseout:(e)=>{
          e.target.closePopup();
          setSelectedTargetId(null);
        },
        mousedown:(e)=>{
          e.target.openPopup();
          setSelectedTargetId(target._id);
        }
      }}
    >
      <Popup>
        <Typography variant="body2">
          Number: { 
            target.outpostNums.length > 1 ? target.trackId : target.relatedTargets[0]?.TGT_NUM
          }
          <br/>
            Outpost: { target.outpostNums?.join(", ") }
          <br/>
          {
            address !== null && address > 0 &&
            <>
              Address: {addressHex}
              <br/>
            </>
          } 
          {
            content !== null && content > 0 &&
            <>
              Content: {contentOct}
              <br/>
            </>
          }
          {
            hasCountry &&
            <>
              Country:
              <span style={{marginLeft:6}}>
                <span
                  className={`fi fi-${countryInfo.iso}`}
                  style={{marginRight:6}}
                />
                {countryInfo.name}
              </span>
            <br/>
            </>
          }
          Latitude: {toDMS(position[0], true)}
          <br/>
          Longitude: {toDMS(position[1], false)}
          <br/>
          Direction: {target.direction}°
          <br/>
          Ground Speed: {
            speedUnit === SpeedUnits.KNOTS ? 
            (target.velocity / 0.514444).toFixed(1) 
            : speedUnit === SpeedUnits.KMH ? 
            (target.velocity * 3.6).toFixed(1) : 
            (target.velocity * 2.23694).toFixed(1)} {
              speedUnit === SpeedUnits.KNOTS ? 
              "kts" : 
              speedUnit === SpeedUnits.KMH ? 
              "km/h" : "mph"
          }
          <br/>
          Altitude: {target.height} m
          <br/>
          Updated: {
            format(
              new Date(target.modifiedAt),
              "dd/MM/yyyy HH:mm"
            )
          }
        </Typography>
      </Popup>
    </Marker>
  );              
};

export default React.memo(FlightMarkerItem);